import React, { ReactElement, useState } from "react";
import { Button, Col, Form, Row } from "react-bootstrap";
import { Application, ApplicationService } from "../";

import DatePicker from "react-datepicker";
import useForceUpdate from "use-force-update";

import { CountryDropdown } from "react-country-region-selector";
import { date, object, string } from "yup";
import { InputErrorMessage, Loading } from "../../ui";

import "react-datepicker/dist/react-datepicker.css";

interface IProps {
	application: Application;
	jumpToStep?: any;
	nextStep: number;
}

export const StepThreeIndividual = (props: IProps): ReactElement => {
	const [errors, setErrors] = useState(null);
	const forceUpdate = useForceUpdate();

	const applicationService = new ApplicationService();
	const application = props.application;

	let courses = [
		"Financial Securities Management",
		"Microsoft Office (Intermediate to Advance)",
		"Human Resource Management",
		"Microsoft Office (Beginners to Intermediate)",
		"Payroll, Staff Benefits and Statutory Deductions",
		"Records &amp; Information Management",
		"Supervisory Management",
	];

	if (application.applyingFor === "Module") {
		courses = [
			"Introduction to Business Administration",
			"Fundamentals of Accounting",
			"Occupational Safety & Health",
			"Social Work Theories and Policy Framework",
			"Audit Practice and Procedures I",
			"Financial Accounting",
			"Production and Operations Management",
			"Industrial and Employee Relations",
			"Human Resource Counselling",
			"Warehouse & Inventory Management",
			"Other",
		];
	}

	const genders = ["Male", "Female", "Other"];

	const studenStatuses = [
		"First time attending UCC",
		"I previously attended UCC",
		"I am currently attending UCC",
	];

	if (!application.lead) {
		return <Loading />;
	}

	const validateStep = () => {
		setErrors(null);
		if (!application.lead) {
			return;
		}

		const schemaOptions = {
			firstName: string().required("First Name is a required field"),
			lastName: string().required("Last Name is a required field"),
			email: string()
				.email("Email must be a valid email")
				.required("Email is a required field"),
			country: string().required("Country is a required field"),
			city: string().required("City is a required field"),
			street: string().required("Street address is a required field"),
			phone: string().required("Phone is a required field"),
			course: string().required("Please select a course"),
			birthday: date().required("Birthday is a required field"),
			gender: string().required("Gender is a required field"),
			studentStatus: string().required(
				"Student status is a required field",
			),
			uccIdNumber: string().when("studentStatus", {
				is: val => {
					return (
						val === studenStatuses[1] || val === studenStatuses[2]
					);
				},
				then: string().required("Please enter your UCC ID Number."),
			}),
			courseOther: string().when("course", {
				is: "Other",
				then: string().required("Please enter a course name."),
			}),
		};

		const schemaObject = {
			firstName: application.lead.firstName,
			lastName: application.lead.lastName,
			email: application.lead.email,
			country: application.lead.country,
			city: application.lead.city,
			street: application.lead.street,
			phone: application.lead.phone,
			course: application.courseName,
			birthday: application.lead.birthday,
			gender: application.lead.gender,
			studentStatus: application.studentStatus,
			uccIdNumber: application.lead.uccIdNumber,
			courseOther: application.courseOther,
		};

		const schema = object(schemaOptions);

		if (application.lead) {
			schema
				.validate(schemaObject, { abortEarly: false })
				.then(valid => {
					if (valid) {
						setErrors(null);
						if (props.application.currentStep < 3) {
							props.application.currentStep = 3;
						}
						applicationService.updateApplication(application);
						props.jumpToStep(props.nextStep);
					}
				})
				.catch(err => {
					setErrors(err);
				});
		}
	};

	return (
		<>
			<Row className="mb-3">
				<Col>
					<Form.Group controlId="course">
						<Form.Label>Course</Form.Label>
						<Form.Control
							size="lg"
							as="select"
							defaultValue={application.courseName}
							onChange={(event: any) => {
								application.courseName = event.target.value;
								forceUpdate();
							}}
						>
							<option>-Select-</option>
							{courses.map(course => (
								<option value={course}>{course}</option>
							))}
						</Form.Control>
						<InputErrorMessage errors={errors} field="course" />
					</Form.Group>
				</Col>
			</Row>
			{application.courseName === "Other" ? (
				<Row className="mb-3">
					<Col>
						<Form.Group controlId="courseOther">
							<Form.Label>Other Course</Form.Label>
							<Form.Control
								size="lg"
								defaultValue={application.courseOther}
								onChange={(event: any) => {
									application.courseOther =
										event.target.value;
									forceUpdate();
								}}
							/>
							<InputErrorMessage
								errors={errors}
								field="courseOther"
							/>
						</Form.Group>
					</Col>
				</Row>
			) : null}
			<Row className="mb-3">
				<Col>
					<Form.Group controlId="firstName">
						<Form.Label>First Name</Form.Label>
						<Form.Control
							size="lg"
							defaultValue={application.lead.firstName}
							disabled
						/>
						<InputErrorMessage errors={errors} field="firstName" />
					</Form.Group>
				</Col>
				<Col>
					<Form.Group controlId="lastName">
						<Form.Label>Last Name</Form.Label>
						<Form.Control
							size="lg"
							disabled
							defaultValue={application.lead.lastName}
						/>
						<InputErrorMessage errors={errors} field="lastName" />
					</Form.Group>
				</Col>
			</Row>
			<Row>
				<Col sm={8} className="mb-3">
					<Form.Group controlId="street">
						<Form.Label>Street address</Form.Label>
						<Form.Control
							size="lg"
							defaultValue={application.lead.street}
							onChange={(event: any) => {
								if (application.lead) {
									application.lead.street =
										event.target.value;
									forceUpdate();
								}
							}}
						/>
						<InputErrorMessage errors={errors} field="street" />
					</Form.Group>
				</Col>
			</Row>

			<Row>
				<Col sm={6} className="mb-3">
					<Form.Group controlId="city">
						<Form.Label>City</Form.Label>
						<Form.Control
							size="lg"
							defaultValue={application.lead.city}
							onChange={(event: any) => {
								if (application.lead) {
									application.lead.city = event.target.value;
									forceUpdate();
								}
							}}
						/>
						<InputErrorMessage errors={errors} field="city" />
					</Form.Group>
				</Col>
			</Row>
			<Row>
				<Col sm={8} className="mb-3">
					<Form.Group controlId="country">
						<Form.Label>Country</Form.Label>
						<CountryDropdown
							classes="form-control form-control-lg"
							value={application.lead.country as string}
							priorityOptions={["JM"]}
							onChange={val => {
								if (application.lead) {
									application.lead.country = val;
									forceUpdate();
								}
							}}
						/>
						<InputErrorMessage field="country" errors={errors} />
					</Form.Group>
				</Col>
			</Row>
			<Row>
				<Col sm={8} className="mb-3">
					<Form.Group controlId="email">
						<Form.Label>Email</Form.Label>
						<Form.Control
							size="lg"
							disabled
							defaultValue={application.lead.email}
						/>
						<InputErrorMessage errors={errors} field="email" />
					</Form.Group>
				</Col>
			</Row>
			<Row>
				<Col sm={8} className="mb-3">
					<Form.Group controlId="phone">
						<Form.Label>Mobile Number</Form.Label>
						<Form.Control
							size="lg"
							defaultValue={application.lead.phone}
							onChange={(event: any) => {
								if (application.lead) {
									application.lead.phone = event.target.value;
									forceUpdate();
								}
							}}
						/>
						<InputErrorMessage errors={errors} field="phone" />
					</Form.Group>
				</Col>
			</Row>
			<Row>
				<Col sm={8} className="mb-3">
					<Form.Group controlId="birthday">
						<Form.Label> Date of Birth </Form.Label>
						<DatePicker
							showYearDropdown
							className="form-control form-control-lg"
							selected={application.lead.birthday}
							onChange={(d: Date) => {
								if (application.lead) {
									application.lead.birthday = d;
									forceUpdate();
								}
							}}
						/>
						<InputErrorMessage errors={errors} field="birthday" />
					</Form.Group>
				</Col>
			</Row>
			<Row>
				<Col sm={8} className="mb-3">
					<Form.Group controlId="gender">
						<Form.Label>Gender</Form.Label>
						<Form.Control
							as="select"
							size="lg"
							defaultValue={application.lead.gender}
							onChange={(event: any) => {
								if (application.lead) {
									application.lead.gender =
										event.target.value;
									forceUpdate();
								}
							}}
						>
							<option>-Select-</option>
							{genders.map(gender => (
								<option value={gender}>{gender}</option>
							))}
						</Form.Control>
						<InputErrorMessage errors={errors} field="gender" />
					</Form.Group>
				</Col>
			</Row>
			<Row>
				<Col sm={12} className="mb-3">
					<strong className="mb-3" style={{ display: "block" }}>
						Student Status
					</strong>
					{studenStatuses.map((option, idx) => (
						<>
							<div key={`default-${idx}`} className="mb-3">
								<Form.Check
									type="radio"
									id={`default-${idx}`}
									name="studentStatus"
									label={option}
									value={option}
									defaultChecked={
										application.studentStatus === option
									}
									onChange={() => {
										application.studentStatus = option;
										forceUpdate();
									}}
								/>
							</div>
						</>
					))}
					<InputErrorMessage field="studentStatus" errors={errors} />
				</Col>
				{application.studentStatus &&
				application.studentStatus !== studenStatuses[0] ? (
					<Form.Group controlId="uccIdNumber">
						<Form.Label>Enter your UCC ID number *</Form.Label>
						<Form.Control
							size="lg"
							defaultValue={application.lead.uccIdNumber}
							onChange={(event: any) => {
								if (application.lead) {
									application.lead.uccIdNumber =
										event.target.value;
									forceUpdate();
								}
							}}
						/>
						<InputErrorMessage
							errors={errors}
							field="uccIdNumber"
						/>
					</Form.Group>
				) : null}
				<Col sm={12} />
			</Row>

			<Row>
				<Col>
					<Button
						variant="primary"
						size="lg"
						className="pull-left btn-next"
						onClick={() => props.jumpToStep(props.nextStep - 2)}
					>
						Previous
					</Button>
					<Button
						variant="primary"
						size="lg"
						className="float-right btn-next"
						onClick={validateStep}
					>
						Next
					</Button>
				</Col>
			</Row>
		</>
	);
};
